import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { api } from "../api";
import { canManageFamily, isRestrictedChild, safeNext, useMe, type Me } from "../me";
import { PocketFrame } from "../shell";
import { readMicLocal, writeMicLocal } from "./mic";
import { lockParent, PinUnlock } from "./PinGate";
import type { FamilyLearner, FamilyPayload } from "./types";

const AGE_BANDS = ["5-6", "7-9", "10-12", "13+"];
const AVATARS = [
  { key: "owl", label: "Сова", emoji: "🦉" },
  { key: "fox", label: "Лиса", emoji: "🦊" },
  { key: "cat", label: "Кот", emoji: "🐱" },
  { key: "bear", label: "Медведь", emoji: "🐻" },
];

function avatarEmoji(key: string | null): string {
  return AVATARS.find((a) => a.key === key)?.emoji ?? "🙂";
}

export function FamilyPage() {
  const [me, setMe] = useMe();
  const loc = useLocation();
  const next = useMemo(() => safeNext(new URLSearchParams(loc.search).get("next")), [loc.search]);
  const prefill = useMemo(() => new URLSearchParams(loc.search).get("invite") ?? "", [loc.search]);
  const [family, setFamily] = useState<FamilyPayload | null>(null);
  const [name, setName] = useState("");
  const [ageBand, setAgeBand] = useState("7-9");
  const [avatar, setAvatar] = useState("owl");
  const [mode, setMode] = useState<"shared" | "invite">("shared");
  const [pin, setPin] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteFor, setInviteFor] = useState("");
  const [issued, setIssued] = useState<{ code: string; expiresIn: string } | null>(null);
  const [acceptCode, setAcceptCode] = useState(prefill);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [micLocal, setMicLocal] = useState<Record<string, boolean>>({});

  const reload = () => api<FamilyPayload>("/api/v1/family").then(setFamily);

  useEffect(() => {
    reload().catch(() => setFamily({ householdId: null, hasPin: false, learners: [] }));
  }, []);

  useEffect(() => {
    if (!family) return;
    const nextLocal: Record<string, boolean> = {};
    for (const l of family.learners) {
      const stored = readMicLocal(l.id);
      nextLocal[l.id] = stored ?? Boolean(l.mic_granted);
    }
    setMicLocal(nextLocal);
  }, [family]);

  if (me === undefined) return <PocketFrame className="family-page"><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(`/family${loc.search}`)}`} />;

  const manage = canManageFamily(me, Boolean(family?.hasPin));
  const childAccount = isRestrictedChild(me);
  const needsUnlock = Boolean(family?.hasPin) && !me.parentUnlocked && !childAccount;

  async function refreshMe() {
    const nextMe = await api<Me>("/api/v1/me");
    setMe(nextMe);
    await reload();
  }

  return (
    <PocketFrame className="family-page">
    <div className="page">
      <p className="row">
        <a href="/app">Сегодня</a>
        <a href="/family/rewards">Награды</a>
        <a href="/family/privacy">Конфиденциальность и удаление данных</a>
      </p>
      <h1>Моя семья</h1>
      <p>Управляйте профилями детей и связанными аккаунтами.</p>

      {needsUnlock ? <PinUnlock onUnlocked={(nextMe) => { setMe(nextMe); void reload(); }} /> : null}
      {me.parentUnlocked ? (
        <p className="row">
          <button type="button" className="btn ghost" onClick={() => void lockParent((nextMe) => { setMe(nextMe); void reload(); })}>
            Закрыть раздел взрослого
          </button>
        </p>
      ) : null}

      <section>
        <h2>Кто сейчас занимается?</h2>
        {family?.learners.length ? family.learners.map((l) => (
          <LearnerCard
            key={l.id}
            learner={l}
            manage={manage}
            childAccount={childAccount}
            micOn={Boolean(micLocal[l.id])}
            next={next}
            onMic={async (granted) => {
              writeMicLocal(l.id, granted);
              setMicLocal((prev) => ({ ...prev, [l.id]: granted }));
              if (manage) {
                await api("/api/v1/family/consent/mic", { method: "POST", body: JSON.stringify({ learnerId: l.id, granted }) });
                await reload();
              }
            }}
            onUnlink={async () => {
              const ok = window.confirm(
                `Разорвать семейную связь?\nВзрослый больше не увидит прогресс и награды ${l.nickname}. Аккаунт ребёнка и его учебная история сохранятся.`,
              );
              if (!ok) return;
              const res = await api<{ accessEndsAt?: string }>("/api/v1/family/unlink", {
                method: "POST",
                body: JSON.stringify({ learnerId: l.id }),
              });
              const date = res.accessEndsAt ? new Date(res.accessEndsAt).toLocaleDateString("ru-RU") : "";
              setMsg(date ? `Доступ, предоставленный этой семьёй, завершится ${date}.` : "Семейная связь разорвана.");
              await reload();
            }}
          />
        )) : <p className="muted">Профилей пока нет.</p>}
      </section>

      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          try {
            await api("/api/v1/family/invites/accept", { method: "POST", body: JSON.stringify({ code: acceptCode.trim() }) });
            setMsg("Аккаунт связан с семьёй.");
            await refreshMe();
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <h2>Связать аккаунт ребёнка</h2>
        <p>Введите код, который дал взрослый.</p>
        <p>Взрослый сможет видеть ваш учебный прогресс, управлять доступом и домашними наградами.</p>
        <label className="field">Код приглашения<input value={acceptCode} onChange={(e) => setAcceptCode(e.target.value.toUpperCase())} /></label>
        {err ? <p className="alert">{err}</p> : null}
        <button className="btn" type="submit">Присоединиться</button>
      </form>

      {manage ? (
        <>
          <form
            className="card"
            onSubmit={async (e) => {
              e.preventDefault();
              setErr("");
              const created = await api<{ id: string }>("/api/v1/family/learners", {
                method: "POST",
                body: JSON.stringify({ nickname: name, ageBand, avatarKey: avatar }),
              });
              setMsg(`Профиль ${name} создан`);
              setName("");
              if (mode === "invite") {
                const inv = await api<{ code: string; expiresIn: string }>("/api/v1/family/invites", {
                  method: "POST",
                  body: JSON.stringify({ learnerId: created.id }),
                });
                setIssued(inv);
              }
              await reload();
            }}
          >
            <h2>Добавить ребёнка</h2>
            <label className="field">
              Имя или псевдоним
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <p className="muted">Так имя будет показано внутри семьи. Фамилия не нужна.</p>
            <label className="field">
              Возрастная группа
              <select value={ageBand} onChange={(e) => setAgeBand(e.target.value)}>
                {AGE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="field">
              Аватар
              <select value={avatar} onChange={(e) => setAvatar(e.target.value)}>
                {AVATARS.map((a) => <option key={a.key} value={a.key}>{a.emoji} {a.label}</option>)}
              </select>
            </label>
            <fieldset className="field">
              <legend>Как подключить профиль</legend>
              <label><input type="radio" name="mode" checked={mode === "shared"} onChange={() => setMode("shared")} /> Профиль на семейном аккаунте</label>
              <label><input type="radio" name="mode" checked={mode === "invite"} onChange={() => setMode("invite")} /> Отдельный аккаунт по приглашению</label>
            </fieldset>
            <button className="btn" type="submit">Добавить ребёнка</button>
          </form>

          <form
            className="card"
            onSubmit={async (e) => {
              e.preventDefault();
              const inv = await api<{ code: string; expiresIn: string }>("/api/v1/family/invites", {
                method: "POST",
                body: JSON.stringify({ learnerId: inviteFor || undefined }),
              });
              setIssued(inv);
            }}
          >
            <h2>Связать существующий аккаунт</h2>
            <p>Создайте одноразовое приглашение. Ребёнок примет его после входа.</p>
            <label className="field">
              Профиль
              <select value={inviteFor} onChange={(e) => setInviteFor(e.target.value)}>
                <option value="">Новый профиль при принятии кода</option>
                {family?.learners.map((l) => (
                  <option key={l.id} value={l.id}>{l.nickname}</option>
                ))}
              </select>
            </label>
            <button className="btn" type="submit">Создать приглашение</button>
          </form>
        </>
      ) : !childAccount && needsUnlock ? (
        <p className="muted">Покупки, награды и настройки доступны только после PIN.</p>
      ) : null}

      {issued ? (
        <div className="card">
          <p>Код действует {issued.expiresIn}.</p>
          <p className="invite-code">{issued.code}</p>
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => void navigator.clipboard?.writeText(issued.code)}
            >
              Скопировать код
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                const url = `${window.location.origin}/family?invite=${issued.code}`;
                void navigator.clipboard?.writeText(url);
              }}
            >
              Отправить ссылку
            </button>
            <button type="button" className="btn ghost" onClick={() => setIssued(null)}>Отменить приглашение</button>
          </div>
        </div>
      ) : null}

      {childAccount ? null : (
        <form
          className="card"
          onSubmit={async (e) => {
            e.preventDefault();
            await api("/api/v1/family/pin", { method: "POST", body: JSON.stringify({ pin }) });
            setPin("");
            await reload();
          }}
        >
          <h2>{family?.hasPin ? "Код взрослого" : "Создайте код взрослого"}</h2>
          <p>Код защищает покупки, награды и настройки при использовании общего устройства.</p>
          <label className="field">
            Код из 4 цифр
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              disabled={Boolean(family?.hasPin) && !manage}
            />
          </label>
          <p className="muted">Не используйте дату рождения ребёнка.</p>
          <button className="btn ghost" type="submit" disabled={Boolean(family?.hasPin) && !manage}>
            {family?.hasPin ? "Обновить код взрослого" : "Создать код взрослого"}
          </button>
        </form>
      )}

      {msg ? <p>{msg}</p> : null}

      <section className="card">
        <h2>Кто видит данные ребёнка</h2>
        <p>Связанные взрослые видят прогресс, доступ и домашние награды.</p>
        <p>Репетитор видит только агрегированную статистику. Ему недоступны имя ребёнка, email семьи, отдельные ответы, история занятий, награды и голос.</p>
        <p>Временные записи голоса не отправляются на сервер.</p>
      </section>
    </div>
    </PocketFrame>
  );
}

function LearnerCard({
  learner,
  manage,
  childAccount,
  micOn,
  next,
  onMic,
  onUnlink,
}: {
  learner: FamilyLearner;
  manage: boolean;
  childAccount: boolean;
  micOn: boolean;
  next: string;
  onMic: (granted: boolean) => Promise<void>;
  onUnlink: () => Promise<void>;
}) {
  const linked = Boolean(learner.linked_user_id);
  return (
    <div className="card">
      <p><span className="avatar-dot" aria-hidden="true">{avatarEmoji(learner.avatar_key)}</span> {learner.nickname}</p>
      <p className="muted">{linked ? "Отдельный аккаунт по приглашению" : "Профиль на семейном аккаунте"}</p>
      <div className="row">
        <button
          className="btn"
          type="button"
          onClick={() => {
            void api("/api/v1/family/active-profile", { method: "POST", body: JSON.stringify({ learnerId: learner.id }) })
              .then(() => api("/api/v1/me"))
              .then(() => location.assign(next));
          }}
        >
          Продолжить как {learner.nickname}
        </button>
        {linked && (manage || childAccount) ? (
          <button className="btn ghost" type="button" onClick={() => void onUnlink()}>Разорвать семейную связь</button>
        ) : null}
      </div>
      <label className="mic-toggle">
        <input
          type="checkbox"
          checked={micOn}
          disabled={!manage}
          onChange={(e) => void onMic(e.target.checked)}
        />
        Разрешение временной записи
      </label>
      <p className="muted">Запись остаётся только в текущем упражнении. Временные записи голоса не отправляются на сервер.</p>
    </div>
  );
}
